import { tryOnScopeDispose, tryOnUnmounted } from '@vueuse/core';
import snakeCase from 'lodash/snakeCase';
import { computed, nextTick, readonly, ref } from 'vue';
import type { LocationQuery } from 'vue-router';
import { isNavigationFailure, useRoute, useRouter } from 'vue-router';

import { useAuthStore } from '@/stores/auth';
import { useTrackingStore } from '@/stores/tracking';
import { useUtmStore } from '@/stores/utm';

type BeforeBrandSwitchGuard = () => boolean | Promise<boolean>;

const copyQueryParams = ref(false);
const includeQueryParams = ref<string[]>([]);
const excludeQueryParams = ref<string[]>([]);
const isBrandSwitching = ref(false);
const beforeBrandSwitchGuards = new Set<BeforeBrandSwitchGuard>();

const includeQueryParamsToCheck = computed(() =>
  includeQueryParams.value.flatMap((param) => [param, snakeCase(param)]),
);

const excludeQueryParamsToCheck = computed(() =>
  excludeQueryParams.value.flatMap((param) => [param, snakeCase(param)]),
);

export function useBrandSwitcher() {
  const route = useRoute();
  const router = useRouter();
  const utmStore = useUtmStore();
  const authStore = useAuthStore();
  const trackingStore = useTrackingStore();

  function enableCopyQueryParams({
    include,
    exclude,
  }: { include?: string[]; exclude?: string[] } = {}) {
    copyQueryParams.value = true;
    includeQueryParams.value = Array.isArray(include) ? include : [];
    excludeQueryParams.value = Array.isArray(exclude) ? exclude : [];
  }

  function filterQueryParams(query: LocationQuery = {}) {
    if (!copyQueryParams.value) {
      return undefined;
    }

    return Object.fromEntries(
      Object.entries(query).filter(
        ([param]) =>
          (!includeQueryParamsToCheck.value?.length ||
            includeQueryParamsToCheck.value.includes(param)) &&
          !(excludeQueryParamsToCheck.value ?? []).includes(param),
      ),
    );
  }

  async function performBrandSwitch(brandLabel: string, query?: LocationQuery) {
    isBrandSwitching.value = true;

    try {
      const result = await router.push({
        name: route.name,
        params: { brandLabel },
        query,
      });

      if (!isNavigationFailure(result)) {
        void authStore.setCurrentBrand({ brandLabel });
      }

      return result;
    } finally {
      isBrandSwitching.value = false;
    }
  }

  async function runBeforeBrandSwitchGuards(): Promise<boolean> {
    for (const guard of beforeBrandSwitchGuards) {
      if (!(await guard())) return false;
    }
    return true;
  }

  function registerBeforeBrandSwitch(guard: BeforeBrandSwitchGuard): () => void {
    beforeBrandSwitchGuards.add(guard);
    const unregister = () => beforeBrandSwitchGuards.delete(guard);
    tryOnScopeDispose(unregister);
    return unregister;
  }

  async function switchBrand(brandLabel: string) {
    if (!(await runBeforeBrandSwitchGuards())) {
      return;
    }

    if (route?.params?.brandLabel) {
      const query = filterQueryParams(route.query);

      if (utmStore.isManagementSettingsDirty) {
        void utmStore.confirmLeaving(async () => {
          await performBrandSwitch(brandLabel, query);
        });
      } else {
        await performBrandSwitch(brandLabel, query);
      }
    } else {
      void authStore.setCurrentBrand({ brandLabel });
      void nextTick(() => {
        // faking a Page Viewed event to keep metrics consistent
        trackingStore.trackPageViewedEvent(route, null);
      });
    }
  }

  tryOnUnmounted(() => {
    copyQueryParams.value = false;
    includeQueryParams.value = [];
    excludeQueryParams.value = [];
  });

  return {
    isBrandSwitching: readonly(isBrandSwitching),
    enableCopyQueryParams,
    filterQueryParams,
    registerBeforeBrandSwitch,
    switchBrand,
  };
}
